'use client'

import { ext } from '@/utils/linkTarget'
import CoachingPage from '@/components/organisms/sportPage/coachingPage'

const PLAYTOMIC_ACADEMY = 'https://app.playtomic.io/tenants/325afbbf-2acc-473b-b93c-0dd30b4adff3/academy'

const FALLBACK_COACHES = [
  {
    name: 'Uriol Padro',
    position: 'Padel Director',
    bio: 'Uriol leads the padel program at Mahima Bali and is focused on developing players across every level of the game. From Bronze level players to Silver, Gold, and advanced competitive athletes, Uriol creates personalized coaching experiences that help players improve technically, tactically, and physically.',
    bio2: 'Uriol creates a professional but enjoyable training environment where players can improve quickly while still enjoying the social and energetic side of padel.',
    focusAreas: [
      { label: 'Technical improvement' },
      { label: 'Match strategy and positioning' },
      { label: 'Confidence building' },
      { label: 'Game intelligence' },
      { label: 'Competitive preparation' },
      { label: 'Communication and teamwork' },
    ],
  },
]

const FALLBACK_CLASSES = [
  {
    title: 'Kids Academy',
    description: 'A structured program for young players designed to build the fundamentals of padel in a fun, encouraging environment. Sessions focus on coordination, technique, and a love for the game.',
    price: '',
    duration: '60 min',
    playtomicUrl: 'https://playtomic.io',
  },
  {
    title: 'Group Lesson',
    description: 'Small group sessions open to all levels. Work on your game alongside other players with guided drills, point play, and tactical feedback from Uriol.',
    price: '',
    duration: '90 min',
    playtomicUrl: 'https://playtomic.io',
  },
  {
    title: 'Private Lesson',
    description: 'One-on-one coaching tailored entirely to your level and goals. The fastest way to improve — Uriol focuses fully on your technique, tactics, and mental game.',
    price: '',
    duration: '60 min',
    playtomicUrl: 'https://playtomic.io',
  },
]

// ── Coach Block ────────────────────────────────────────────────────────────

const CoachBlock = ({ coach, cta }) => {
  const hasPhoto = !!coach.photo?.asset?._ref
  const focusAreas = coach.focusAreas?.length ? coach.focusAreas.map(f => f.label) : []
  return (
    <Container outerClassName="mt-mediumMobile lg:mt-largeDesktop">
      <Spacer />
      <div className="mt-mediumMobile lg:mt-largeDesktop flex flex-col lg:flex-row gap-[40px] lg:gap-[60px]">
        <div className="flex flex-col gap-smallMobile flex-1">
          <h2 className="text-subheadingMobile md:text-subheadingDesktop font-ttDrugsTrial text-olive uppercase">
            {coach.name}
          </h2>
          {coach.position && (
            <p className="text-bodyMobile md:text-bodyDesktop font-chivoMono text-khaki">{coach.position}</p>
          )}
          {coach.bio && (
            <p className="text-bodyMobile md:text-bodyDesktop font-chivoMono text-khaki max-w-[480px]">{coach.bio}</p>
          )}
          {coach.bio2 && (
            <p className="text-bodyMobile md:text-bodyDesktop font-chivoMono text-khaki max-w-[480px]">{coach.bio2}</p>
          )}
          {focusAreas.length > 0 && (
            <ul className="flex flex-col mt-detailsDesktop">
              <li>
                <p className="text-bodyMobile md:text-bodyDesktop font-chivoMono text-khaki uppercase tracking-[3.2px] mb-[14px]">
                  Coaching focuses on
                </p>
              </li>
              {focusAreas.map((area) => (
                <li key={area} className="flex items-center gap-[12px] border-t border-dune py-[10px]">
                  <span className="text-bodyMobile md:text-bodyDesktop font-chivoMono text-olive">{area}</span>
                </li>
              ))}
              <li className="border-t border-dune" />
            </ul>
          )}
          <div className="flex flex-wrap gap-[16px] mt-detailsDesktop">
            <a
              href={cta?.playtomicUrl || 'https://playtomic.io'}
              {...ext(cta?.playtomicUrl || 'https://playtomic.io')}
              className="inline-flex items-center px-[30px] py-[16px] border border-olive text-bodyMobile md:text-rectangleButtonDesktop font-chivoMono text-olive uppercase tracking-[3.2px] hover:bg-olive hover:text-background transition-colors duration-300 whitespace-nowrap"
            >
              Book on Playtomic
            </a>
            <a
              href="https://wa.me/6281337582672"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-[30px] py-[16px] bg-olive text-bodyMobile md:text-rectangleButtonDesktop font-chivoMono text-background uppercase tracking-[3.2px] hover:bg-umber transition-colors duration-300 whitespace-nowrap"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
        {hasPhoto && (
          <div className="relative w-full lg:max-w-[520px] aspect-[5/4] shrink-0">
            <Image
              src={urlForImage(coach.photo).url()}
              alt={coach.photo.alt || `${coach.name} — Mahima Bali`}
              fill
              placeholder={coach.photo.metadata?.lqip ? 'blur' : 'empty'}
              blurDataURL={coach.photo.metadata?.lqip}
              className="object-cover"
            />
          </div>
        )}
      </div>
    </Container>
  )
}

const DEFAULT_INTRO =
  'Improve your padel in Ubud with private lessons, group classes, and a dedicated Kids Academy — all on WPT-standard courts at Mahima Bali. Led by Uriol Padro, our coaching program develops players of every level, from first-time players to competitive athletes looking to sharpen their game in Sayan, Ubud.'

const PadelCoachingPage = ({ coachingAPI, footerAPI, settingAPI }) => {
  const hero = coachingAPI?.hero || {}
  const cta = coachingAPI?.cta || {}
  const coaches = (coachingAPI?.coaches?.length ? coachingAPI.coaches : FALLBACK_COACHES).map((c) => ({
    ...c,
    focusAreas: (c.focusAreas || []).map((f) => f.label).filter(Boolean),
  }))
  const classes = (coachingAPI?.classes?.length ? coachingAPI.classes : FALLBACK_CLASSES).map((c) => ({
    ...c,
    url: c.playtomicUrl && c.playtomicUrl !== 'https://playtomic.io' ? c.playtomicUrl : PLAYTOMIC_ACADEMY,
  }))
  const bookUrl = cta.playtomicUrl || PLAYTOMIC_ACADEMY

  return (
    <CoachingPage
      footerAPI={footerAPI}
      settingAPI={settingAPI}
      eyebrow={hero.label || 'Padel coaching in Sayan, Ubud'}
      heading={hero.heading || 'Padel Coaching at Mahima'}
      intros={[hero.intro || (!coachingAPI ? DEFAULT_INTRO : null), hero.intro2]}
      classesHeading={coachingAPI?.classesHeading || 'Classes & Sessions'}
      classes={classes}
      classesImage={coachingAPI?.classesImage}
      classesImageAlt="Padel coaching at Mahima, Sayan, Ubud"
      coachesHeading={coachingAPI?.teamHeading}
      coaches={coaches}
      bookUrl={bookUrl}
      cta={{
        heading: cta.heading || 'Ready to improve your game?',
        description: cta.description || 'Book a session on our WPT-standard padel courts in Sayan, Ubud. All levels welcome.',
        url: bookUrl,
        label: 'Book a class on Playtomic',
      }}
    />
  )
}

export default PadelCoachingPage
